import { FC, MouseEvent, useState } from "react";
import { Share } from "@capacitor/share";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import IosShareIcon from "@mui/icons-material/IosShare";
import { isOrtexApp } from "@/lib/utils";

const APP_ORIGIN = "https://app.ortex.com";

/** The link to what is on screen now. In the apps the page is app.ortex.com, which is also what opens the app. */
export const currentShareUrl = () => {
    if (typeof window === "undefined") return "";
    const { origin, pathname, search, hash } = window.location;
    return (isOrtexApp() ? APP_ORIGIN : origin) + pathname + search + hash;
};

/** The Clipboard API, falling back to a selected textarea where it is denied (embedded views, older WebViews). */
const copyText = async (text: string, near: Element) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        const area = document.createElement("textarea");
        area.value = text;
        area.setAttribute("readonly", "");
        area.style.position = "fixed";
        area.style.opacity = "0";
        // Inside the open dialog or drawer, whose focus trap would otherwise pull focus back off the textarea.
        (near.closest(".MuiModal-root") || document.body).appendChild(area);
        area.select();
        const ok = document.execCommand("copy");
        area.remove();
        return ok;
    }
};

/**
 * Share the current URL: the native share sheet in the apps, copy to clipboard on the web.
 * Pair it with a deep-linked modal (useModalLink), whose URL is the link to the item.
 */
const ShareLinkButton: FC<{ title: string; size?: "small" | "medium"; onShare?: (method: "native" | "copy") => void }> = ({ title, size = "small", onShare }) => {
    const [copied, setCopied] = useState(false);
    const app = isOrtexApp();

    const share = async (event: MouseEvent) => {
        event.stopPropagation();
        const button = event.currentTarget as Element; // React clears currentTarget once the handler awaits.
        const url = currentShareUrl();
        if (app) {
            try {
                await Share.share({ title, text: title, url, dialogTitle: "Share" });
                onShare?.("native");
            } catch {
                // Dismissed the share sheet.
            }
            return;
        }
        if ((await copyText(url, button)) === false) return; // Blocked: the address bar has the same link.
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        onShare?.("copy");
    };

    return (
        <Tooltip title={app ? "Share" : copied ? "Link copied" : "Copy link"} arrow>
            <IconButton aria-label={app ? "Share" : "Copy link"} onClick={share} size={size} color="inherit">
                <IosShareIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

export default ShareLinkButton;
